/**
 * x-scrollspy directive.
 *
 * Highlights navigation links that correspond to the currently visible section
 * using GSAP ScrollTrigger. Links are matched by their `href` hash to their
 * target element IDs. Multiple links pointing to the same target are all
 * activated simultaneously.
 *
 * ScrollTriggers are shared across directive instances for the same target
 * element to avoid duplicate triggers. Each instance subscribes a callback and
 * the trigger is killed when all subscribers have been removed.
 *
 * On initial load, the directive highlights the last section whose start
 * position is at or before the current scroll position, handling mid-page
 * refreshes, gaps between sections, and anchor links.
 *
 * @options
 *   linkSelector  - CSS selector for anchor elements to track (default: 'a[href^="#"]').
 *   activeClass   - CSS class applied to active links (default: "is-active").
 *   offset        - Pixel offset from the trigger position when `offsetPosition` is "top"
 *                   (default: 100).
 *   offsetPosition - Trigger position: "center" uses the viewport centre as the
 *                    scroll trigger point; "top" uses a pixel offset from the top of
 *                    the target, accounting for `scroll-margin-top` (default: "center").
 *
 * @example
 *   <nav x-scrollspy="{ activeClass: 'active', offsetPosition: 'top', offset: 80 }">
 *     <a href="#introduction">Introduction</a>
 *     <a href="#features">Features</a>
 *   </nav>
 */
(() => {
  /**
   * Global registry mapping target elements to their shared ScrollTrigger entry.
   * Structure: `Element -> { trigger: ScrollTrigger, subscribers: Set<Function> }`.
   * @type {Map<Element, { trigger: object, subscribers: Set<Function> }>}
   */
  const registry = new Map();
  let scrollTriggerRef = null;

  /**
   * Returns an existing registry entry for the target element, or creates a
   * new GSAP ScrollTrigger and entry if none exists.
   * @param {Element} targetEl - The section element to watch.
   * @param {string} offsetPosition - "center" or "top".
   * @param {number} offset - Pixel offset used when `offsetPosition` is "top".
   * @returns {{ trigger: object, subscribers: Set<Function> }}
   */
  const getOrCreateTrigger = (targetEl, offsetPosition, offset) => {
    if (registry.has(targetEl)) {
      return registry.get(targetEl);
    }

    let triggerOffset = offsetPosition;

    if ("top" == offsetPosition) {
      const styles = window.getComputedStyle(targetEl);
      const scrollMargin = parseFloat(styles.scrollMarginTop) || 0;

      triggerOffset = `${offset + scrollMargin}px`;
    }

    const entry = {
      trigger: null,
      subscribers: new Set(),
    };

    entry.trigger = scrollTriggerRef.create({
      trigger: targetEl,
      start: `clamp(top ${triggerOffset})`,
      end: `clamp(bottom ${triggerOffset})`,
      onEnter: () => entry.subscribers.forEach((cb) => cb("enter")),
      onEnterBack: () => entry.subscribers.forEach((cb) => cb("enterBack")),
      onLeaveBack: () => entry.subscribers.forEach((cb) => cb("leaveBack")),
    });

    registry.set(targetEl, entry);

    return entry;
  };

  /**
   * Removes a subscriber callback from a target element's registry entry.
   * Kills and removes the ScrollTrigger when no subscribers remain.
   * @param {Element} targetEl - The target section element.
   * @param {Function} callback - The subscriber callback to remove.
   */
  const unsubscribe = (targetEl, callback) => {
    const entry = registry.get(targetEl);
    if (!entry) return;

    entry.subscribers.delete(callback);

    // Kill the trigger when there are no more subscribers.
    if (entry.subscribers.size === 0) {
      entry.trigger.kill();
      registry.delete(targetEl);
    }
  };

  const root = (Alpine, el, options = {}) => {
    const {
      linkSelector = 'a[href^="#"]',
      activeClass = "is-active",
      offset = 100,
      offsetPosition = "center",
    } = options;

    Alpine.bind(el, () => ({
      /** Initialises ScrollTriggers once the element enters the viewport. */
      ["x-intersect:enter.once"]() {
        this.$nextTick(() => {
          scrollTriggerRef = this.$scrollTrigger;
          this.setupScrollTriggers();
        });
      },

      ["x-data"]() {
        const subscriptions = [];

        return {
          activeLink: null,

          /**
           * Unsubscribes all registered callbacks and clears the subscriptions
           * array on teardown.
           */
          destroy() {
            subscriptions.forEach(({ targetEl, callback }) => {
              unsubscribe(targetEl, callback);
            });
            subscriptions.length = 0;
          },

          /**
           * Reads all matching anchor links, groups them by their target element,
           * and creates a shared ScrollTrigger for each target. Highlights the
           * appropriate initial link based on the current scroll position.
           */
          setupScrollTriggers() {
            const links = Array.from(this.$el.querySelectorAll(linkSelector));

            // Group links by their target element.
            const targetMap = new Map();
            links.forEach((link) => {
              const hash = link.hash;
              if (!hash) return;

              const targetEl = document.getElementById(hash.slice(1));
              if (!targetEl) return;

              if (!targetMap.has(targetEl)) {
                targetMap.set(targetEl, []);
              }
              targetMap.get(targetEl).push(link);
            });

            // Build items array sorted by document position.
            const items = Array.from(targetMap.entries())
              .map(([targetEl, targetLinks]) => ({
                targetEl,
                links: targetLinks,
              }))
              .sort((a, b) => {
                const aTop = a.targetEl.getBoundingClientRect().top;
                const bTop = b.targetEl.getBoundingClientRect().top;
                return aTop - bTop;
              });

            items.forEach((item, index) => {
              const { targetEl, links: targetLinks } = item;

              const entry = getOrCreateTrigger(
                targetEl,
                offsetPosition,
                offset,
              );

              const callback = (event) => {
                if (event === "enter" || event === "enterBack") {
                  this.setActiveLinks(targetLinks);
                } else if (event === "leaveBack" && index > 0) {
                  this.setActiveLinks(items[index - 1].links);
                }
              };

              entry.subscribers.add(callback);
              subscriptions.push({ targetEl, callback });
            });

            // On load, highlight the last section whose start we have already scrolled past
            // (handles refresh mid-page, gaps between sections, and anchor links).
            const scrollPos = window.scrollY;
            let initialLinks = items[0]?.links;

            for (let i = items.length - 1; i >= 0; i--) {
              const entry = registry.get(items[i].targetEl);
              if (entry?.trigger && scrollPos >= entry.trigger.start) {
                initialLinks = items[i].links;
                break;
              }
            }

            if (initialLinks) {
              this.setActiveLinks(initialLinks);
            }
          },

          /**
           * Removes the active class and `aria-current` from all tracked links,
           * then applies them to the provided set of links.
           * @param {Element[]} linksToActivate - The links to mark as active.
           */
          setActiveLinks(linksToActivate) {
            const allLinks = this.$el.querySelectorAll(linkSelector);
            allLinks.forEach((l) => {
              l.classList.remove(activeClass);
              l.removeAttribute("aria-current");
            });

            linksToActivate.forEach((link) => {
              link.classList.add(activeClass);
              link.setAttribute("aria-current", "true");
            });
            this.activeLink = linksToActivate[0];
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("scrollspy", (el, { expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};
      root(Alpine, el, options);
    });
  });
})();
